import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, User, Search, Menu, X, PawPrint, LogOut, Package, Shield, Home, Store, MessageCircle, Scissors, Info, ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/products', label: 'Shop', icon: Store },
  { to: '/services', label: 'Services', icon: Scissors },
  { to: '/our-work', label: 'Our Work', icon: ImageIcon },
  { to: '/about', label: 'About Us', icon: Info },
  { to: '/contact', label: 'Contact', icon: MessageCircle },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPos, setTransitionPos] = useState({ x: 50, y: 50 });

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);


  const handleLogout = async () => { await logout(); navigate('/'); };
  
  const handleNavClick = (e, to) => {
    e.preventDefault();
    const x = e.clientX || window.innerWidth / 2;
    const y = e.clientY || window.innerHeight / 2;
    setTransitionPos({ x, y });
    setIsTransitioning(true);
    e.currentTarget.classList.add('btn-pressed');
    
    setTimeout(() => {
      setIsTransitioning(false);
      if (location.pathname === to) window.scrollTo({ top: 0, behavior: 'smooth' });
      else navigate(to);
    }, 800);
  };

  return (
    <nav className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-border-custom" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <PawPrint className="w-7 h-7 text-rust transition-transform duration-300 group-hover:rotate-12" />
            <span className="font-heading text-xl font-bold text-dark-brown">Pets <span className="text-rust">Valley</span></span>
          </Link>

          <div className="hidden lg:flex items-center gap-1.5 bg-pale/60 rounded-full p-1.5" data-testid="nav-links">
            {NAV_LINKS.map(({ to, label, icon: Icon }) => (
              <button key={to} onClick={(e) => handleNavClick(e, to)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-body text-sm font-medium transition-all duration-300 cursor-pointer
                  ${isActive(to) ? 'bg-rust text-cream shadow-md shadow-rust/20' : 'text-mid-brown hover:bg-white hover:text-rust hover:shadow-sm'}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g,'-')}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">


            {user ? (
              <>
                <Link to="/wishlist" className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-border-custom text-mid-brown hover:text-rust hover:border-rust/30 transition-all" data-testid="nav-wishlist"><Heart className="w-4 h-4" /></Link>
                <Link to="/cart" className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-border-custom text-mid-brown hover:text-rust hover:border-rust/30 transition-all" data-testid="nav-cart"><ShoppingCart className="w-4 h-4" /></Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center justify-center w-9 h-9 rounded-full bg-white border border-border-custom text-mid-brown hover:text-rust hover:border-rust/30 transition-all" data-testid="nav-user-menu"><User className="w-4 h-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white">
                    <div className="px-3 py-2 text-sm font-medium text-dark-brown truncate">{user.name || user.email}</div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="nav-profile"><User className="w-4 h-4 mr-2" /> My Profile</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/orders')} className="cursor-pointer" data-testid="nav-orders"><Package className="w-4 h-4 mr-2" /> My Orders</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/wishlist')} className="cursor-pointer"><Heart className="w-4 h-4 mr-2" /> Wishlist</DropdownMenuItem>
                    {user.role === 'admin' && <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="nav-admin"><Shield className="w-4 h-4 mr-2" /> Admin Panel</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="nav-logout"><LogOut className="w-4 h-4 mr-2" /> Logout</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={() => navigate('/login')} size="sm" className="bg-rust hover:bg-dark-brown text-cream font-body rounded-full px-5 h-9 transition-all duration-300" data-testid="nav-login-btn">Sign In</Button>
            )}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden flex items-center justify-center w-9 h-9 rounded-full bg-white border border-border-custom text-mid-brown" data-testid="nav-mobile-toggle">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-border-custom pt-4 animate-fade-up">

            <div className="flex flex-col gap-1.5">
              {NAV_LINKS.map(({ to, label, icon: Icon }) => (
                <button key={to} onClick={(e) => { setMobileMenuOpen(false); handleNavClick(e, to); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm font-medium transition-all text-left ${isActive(to) ? 'bg-rust text-cream' : 'text-mid-brown hover:bg-pale hover:text-rust'}`}
                  data-testid={`nav-mobile-${label.toLowerCase().replace(/\s/g,'-')}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── ULTRA REALISTIC PAGE TRANSITION ── */}
      {isTransitioning && (
        <>
          <div 
            className="page-transition-overlay"
            style={{ 
              '--tx': `${transitionPos.x}px`, 
              '--ty': `${transitionPos.y}px` 
            }}
          />
          <div className="paw-burst-container">
            <div className="paw-burst">🐾</div>
          </div>
        </>
      )}
    </nav>
  );
}
